import React from 'react';
import classNames from 'classnames';
import { Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import ApplicationRecord from '../../../records/application';
import CTAButton from '../../shared/CTAButton';
import ColumnInput from '../../shared/ColumnInput';
import Error from '../../shared/Error';
import StickyIndustryTypeAhead from '../../shared/StickyIndustryTypeAhead';
import StickyInput from '../../shared/StickyInput';
import StickySelect from '../../shared/StickySelect';
import Tooltip from '../../shared/Tooltip';
import useRouteStatus from '../../../hooks/useRouteStatus';
import useSessionEntity from '../../../hooks/useSessionEntity';
import useValidateRoute from '../../../hooks/useValidateRoute';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { asyncActions, handleAsyncError } from '../../../actions/general';
import { getRoute, getSession, loading } from '../../../actions/sessions';
import {
  states,
  business_types,
  poBoxRegex,
} from '../../../lib/constants/SiteVariables';
import { updateApplication } from '../../../actions/applications';
import { validateEin } from '../../../actions/companies';
import { validatePhone } from '../../../actions/users';

export default function Business({ location }) {
  const dispatch = useDispatch();

  const isLoading = useSelector((state) => state.sessions.get('loading'));
  const session = useSelector((state) => state.sessions.get('session'));
  const application = useSessionEntity('application');

  useRouteStatus(location);

  const save = (values) => {
    dispatch(loading(true));
    dispatch(
      asyncActions(
        updateApplication(new ApplicationRecord(values)),
        getSession({ cart: false, prequal: false, merchant: false }),
        getRoute(),
      ),
    ).then((res) => dispatch(loading(false)));
  };

  const checkEin = async (val) => {
    try {
      const res = await dispatch(validateEin(val));
      return res.result;
    } catch (e) {
      dispatch(handleAsyncError(e));
    }
  };

  const checkPhone = async (val) => {
    try {
      const res = await dispatch(validatePhone(val));
      return res.result;
    } catch (e) {
      dispatch(handleAsyncError(e));
    }
  };

  if (!useValidateRoute({ apply: true, company: true })) return <></>;

  return (
    <div id="content" className="my-6 mx-5 sm:mx-auto">
      <div className="text-center mb-8">
        <div className="text-2xl font-avenir-medium">
          Please confirm your business details
        </div>
        {!session.hasCustomFlag('netx') && (
          <div className="font-avenir-medium text-positive-green">
            Remember: applying will <u>not</u> affect your personal credit
          </div>
        )}
      </div>

      <Error />

      <Formik
        // the values that come off the API are null
        // and need to be reset here to cooperate w/ formik
        initialValues={application.toJS()}
        validate={async (values) => {
          const errors = {};
          const addNested = (field, msg) =>
            (errors['business_address'] = {
              ...errors['business_address'],
              [field]: msg,
            });

          if (
            !values.business_address.address1 ||
            values.business_address.address1.trim().length < 2
          ) {
            addNested('address1', '* Required');
          }

          if (poBoxRegex.test(values.business_address.address1.trim())) {
            addNested('address1', '* P.O. Boxes are not accepted.');
          } else if (poBoxRegex.test(values.business_address.address2.trim())) {
            addNested('address2', '* P.O. Boxes are not accepted.');
          }

          if (
            !values.business_address.city ||
            values.business_address.city.trim().length < 2
          ) {
            addNested('city', '* Required');
          }
          if (!values.business_address.state) addNested('state', '* Required');

          if (!values.business_address.zip) {
            addNested('zip', '* Required');
          } else if (values.business_address.zip.trim().length < 5) {
            addNested('zip', '* Must be 5 characters in length');
          }

          if (!values.raw_annual_revenue) {
            errors.raw_annual_revenue = '* Required';
          } else if (
            parseInt(
              values.raw_annual_revenue.replace(/[^\d]/g, ''),
            ) < 1
          ) {
            errors.raw_annual_revenue = '* A valid number is required';
          }

          if (session.hasCustomFlag('netx') && !values.trade_credit_amount_requested) {
            errors.trade_credit_amount_requested = '* Required';
          } else if (
            parseInt(
              values.trade_credit_amount_requested.replace(/[^\d]/g, ''),
            ) < 1
          ) {
            errors.trade_credit_amount_requested = '* A valid number is required';
          }

          if (!values.business_type) errors.business_type = '* Required';
          if (!values.industry) errors.industry = '* Required';

          if (!values.ein) {
            errors.ein = '* Required';
          } else if (values.ein.trim().length < 10) {
            errors.ein = '* Must be 9 characters in length';
          }

          if (!values.business_phone) {
            errors.business_phone = '* Required';
          } else if (values.business_phone.replace(/[^\d]/g, '').length < 10) {
            errors.business_phone = '* Please enter a valid phone number';
          }

          return errors;
        }}
        onSubmit={async (values, { setFieldError }) => {
          dispatch(loading(true));

          const einCheck = await checkEin(values.ein);
          const phoneCheck = await checkPhone(values.phone);

          if (!phoneCheck.valid) {
            setFieldError(
              'business_phone',
              '* Please enter a valid phone number',
            );
          }

          if (!einCheck.valid) {
            setFieldError('ein', einCheck.error);
          }

          einCheck.valid && phoneCheck.valid
            ? save(values)
            : dispatch(loading(false));
        }}
      >
        {({ isSubmitting, errors, setFieldValue, touched, values }) => (
          <Form role="form" className="mx-3 xxs:mx-auto">
            <ColumnInput
              error={
                errors.business_address && errors.business_address.address1
              }
              touched={
                touched.business_address && touched.business_address.address1
              }
            >
              <StickyInput
                id="business_address.address1"
                placeholder="Business Street Address"
                values={values}
              />
            </ColumnInput>

            <ColumnInput>
              <StickyInput
                id="business_address.address2"
                placeholder="Suite or Floor"
                styles="is-mobile"
                values={values}
              />
            </ColumnInput>

            <ColumnInput
              error={errors.business_address && errors.business_address.city}
              touched={
                touched.business_address && touched.business_address.city
              }
            >
              <StickyInput
                id="business_address.city"
                placeholder="City"
                values={values}
              />
            </ColumnInput>

            <ColumnInput
              error={
                errors.business_address &&
                (errors.business_address.state || errors.business_address.zip)
              }
              touched={
                touched.business_address &&
                (touched.business_address.state || touched.business_address.zip)
              }
              className="flex flex-col mobile:flex-row justify-between gap-x-1"
            >
              <StickySelect
                id="business_address.state"
                className="mobile:w-1/2 w-full my-1 mobile:my-0"
                placeholder="State"
                options={states}
                values={values}
              />

              <StickyInput
                id="business_address.zip"
                className="mobile:w-1/2 w-full my-1 mobile:my-0"
                mask="99999"
                type="tel"
                placeholder="Zip"
                values={values}
              />
            </ColumnInput>

            <ColumnInput
              error={errors.raw_annual_revenue}
              touched={touched.raw_annual_revenue}
            >
              <StickyInput
                inputMode="numeric"
                type="tel"
                id="raw_annual_revenue"
                placeholder="$ Total Annual Revenue"
                mask={Number}
                max={999999999999}
                thousandsSeparator=","
                scale={0}
                values={values}
              />
            </ColumnInput>

            <ColumnInput
              error={errors.business_type}
              touched={touched.business_type}
            >
              <StickySelect
                id="business_type"
                options={business_types}
                placeholder="Business Type"
                values={values}
              />
            </ColumnInput>

            <ColumnInput
              error={errors.industry}
              touched={touched.industry}
              className={classNames({
                'mb-5': errors.industry && touched.industry,
              })}
            >
              <StickyIndustryTypeAhead
                id="industry"
                placeholder="Industry"
                setFieldValue={setFieldValue}
                values={values}
              />
            </ColumnInput>

            <ColumnInput
              error={errors.business_phone}
              touched={touched.business_phone}
            >
              <StickyInput
                id="business_phone"
                type="tel"
                mask="(999) 999-9999"
                placeholder="Business Phone Number"
                values={values}
              />
            </ColumnInput>

            <ColumnInput error={errors.ein} touched={touched.ein}>
              <StickyInput
                inputMode="numeric"
                type="tel"
                id="ein"
                mask="99-9999999"
                placeholder="EIN"
                values={values}
                disclaimer={
                  values.business_type === 'Sole Proprietorship' ? (
                    <Tooltip
                      tooltipText="As a sole proprietor, you may enter your social security number (SSN) to identify your business instead of your employer identification number (EIN)."
                      triggerClass="px-1 mt-1 text-[0.6rem] xxs:text-xs hover:cursor-pointer hover:text-primary-blue"
                      containerClass="p-3 px-2 bg-border-gray text-[0.6rem] xxs:text-xs"
                      placement="bottom-start"
                    >
                      <FontAwesomeIcon
                        icon={['fal', 'circle-question']}
                        className="mr-1"
                      />
                      Don't have an EIN?
                    </Tooltip>
                  ) : undefined
                }
              />
            </ColumnInput>

            {session.hasCustomFlag('netx') && (
              <ColumnInput
                error={errors.trade_credit_amount_requested}
                touched={touched.trade_credit_amount_requested}
              >
                <StickyInput
                  inputMode="numeric"
                  type="tel"
                  id="trade_credit_amount_requested"
                  placeholder="$ Trade Credit Requested"
                  mask={Number}
                  max={999999999999}
                  thousandsSeparator=","
                  scale={0}
                  values={values}
                />
              </ColumnInput>
            )}
            <CTAButton
              buttonClass="w-3/4 mobile:w-2/3 mx-auto md:w-full bg-primary-blue font-avenir-black tracking-wider text-white rounded-full uppercase py-3 px-6 text-sm md:text-base inline-block hover:bg-navy-blue flex justify-center items-center"
              buttonText="Next"
              disabled={isLoading}
              isLoading={isLoading}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
}
